import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";

const BOOT_BG = "#3e2723";
const DEFAULT_LOGO = "/logo_sd.png";

export default function Preloader() {
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const { contactInfo, t, lang } = useLanguage();

  const logoUrl =
    typeof contactInfo?.logoUrl === "string" && contactInfo.logoUrl.trim()
      ? contactInfo.logoUrl.trim()
      : DEFAULT_LOGO;

  const tagline = t?.preloader?.tagline || (lang === "en" ? "Write your destiny." : "Scrie-ți destinul.");

  useEffect(() => {
    const rootEl = document.documentElement;
    const { body } = document;
    const appRoot = document.getElementById("root");
    rootEl.classList.add("sd-preloading");
    rootEl.style.backgroundColor = BOOT_BG;
    body.style.backgroundColor = BOOT_BG;
    if (appRoot) appRoot.style.backgroundColor = BOOT_BG;
    return () => {
      rootEl.classList.remove("sd-preloading");
      rootEl.style.backgroundColor = "";
      body.style.backgroundColor = "";
      if (appRoot) appRoot.style.backgroundColor = "";
    };
  }, []);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const total = 1600;
    const tick = (now) => {
      const p = Math.min(100, Math.round(((now - start) / total) * 100));
      setProgress(p);
      if (p < 100) {
        raf = requestAnimationFrame(tick);
      } else {
        setTimeout(() => setVisible(false), 450);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (visible) return undefined;
    const rootEl = document.documentElement;
    const { body } = document;
    const appRoot = document.getElementById("root");
    rootEl.classList.remove("sd-preloading");
    rootEl.style.backgroundColor = "";
    body.style.backgroundColor = "";
    if (appRoot) appRoot.style.backgroundColor = "";
    return undefined;
  }, [visible]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {visible ? (
        <motion.div
          key="preloader"
          initial={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center text-[#fbd9e5]"
          style={{
            backgroundColor: BOOT_BG,
            minHeight: "100dvh",
            width: "100vw",
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
            paddingLeft: "env(safe-area-inset-left)",
            paddingRight: "env(safe-area-inset-right)",
          }}
          data-testid="preloader"
          aria-busy="true"
          aria-live="polite"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 80% 55% at 50% 42%, rgba(251,217,229,0.12) 0%, rgba(62,39,35,0) 70%)",
            }}
          />

          <div className="relative flex w-full max-w-5xl flex-col items-center gap-8 px-6 sm:gap-10">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.08 }}
              className="flex w-full flex-col items-center gap-5 sm:gap-6"
            >
              {/* No mix-blend — keeps logo colors stable */}
              <img
                src={logoUrl}
                alt="Social Destiny"
                className="h-11 w-auto max-w-[min(56vw,200px)] object-contain object-center sm:h-12 sm:max-w-[220px]"
                decoding="async"
                fetchPriority="high"
                onError={(e) => {
                  if (e.currentTarget.getAttribute("src")?.includes(DEFAULT_LOGO)) return;
                  e.currentTarget.src = DEFAULT_LOGO;
                }}
                data-testid="preloader-logo"
              />

              <div className="w-full overflow-hidden">
                <motion.h1
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.18 }}
                  className="whitespace-nowrap text-center font-display font-black leading-none tracking-tighter text-[#fbd9e5] text-[clamp(1.65rem,4.6vw,4.75rem)]"
                >
                  {tagline}
                </motion.h1>
              </div>
            </motion.div>

            <div className="flex w-56 flex-col items-center gap-3 sm:w-64">
              <div className="h-px w-full overflow-hidden bg-[#fbd9e5]/20">
                <motion.div
                  className="h-full bg-[#fbd9e5]"
                  style={{ width: `${progress}%` }}
                  transition={{ ease: "linear" }}
                />
              </div>
              <span className="text-[11px] tabular-nums tracking-[0.28em] text-[#fbd9e5]/65 sm:text-xs">
                {String(progress).padStart(3, "0")}%
              </span>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
